import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type { SafeString } from '@ember/template/-private/handlebars';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enqueueTask } from 'ember-concurrency';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and } from 'ember-truth-helpers';
import EmployeesProfilePhotoUploaderInput from 'garaje/components/employees/profile-photo/uploader/input';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type ActiveStorageService from 'garaje/services/active-storage-extension';
import { localCopy } from 'tracked-toolbox';

interface EmployeesProfilePhotoUploaderModalSignature {
  Args: {
    /**
     * URL for the user/employee's current photo
     */
    currentPhoto?: string | SafeString;
    /**
     * Size of square output image in px (passes through to input)
     */
    outputSize?: number;
    /**
     * Allowed file types (passes through to input)
     */
    validType?: string | RegExp;
    directUploadURL?: string;
    /**
     * S3 path prefix
     */
    prefix?: string;
    includeCredentials?: boolean;
    isLoading?: boolean;
    minimumScale?: number | string;
    maximumScale?: number | string;
    /**
     * Custom validation failure message
     */
    validationError?: string;
    /**
     * Disable upload functionality - shows only Cancel and Remove buttons
     */
    uploadDisabled?: boolean;
    /**
     * Show remove photo button
     */
    showRemoveButton?: boolean;
    /**
     * Show close button (X) in top right corner
     */
    showCloseButton?: boolean;
    /**
     * Action triggered when the modal dismissed
     */
    onClose?: () => void;
    /**
     * Action triggered when Save button clicked
     */
    onSave?: () => void;
    /**
     * Action triggered when "Remove" button clicked
     */
    onRemove?: () => void;
    /**
     * Action triggered when "Remove" button clicked (shows confirmation)
     */
    onRemoveRequest?: () => void;
    /**
     * Action triggered with reference to task when upload initiated
     */
    onUpload?: (task: unknown) => void;
    /**
     * Action triggered when upload error
     */
    onError?: (error: unknown) => void;
    /**
     * Action triggered when upload completed successfully
     */
    onUploadComplete?: (signedId: string | null) => void;
  };
}

export default class EmployeesProfilePhotoUploaderModal extends Component<EmployeesProfilePhotoUploaderModalSignature> {
  // ActiveStorage service: https://github.com/algonauti/ember-active-storage
  @service declare activeStorageExtension: ActiveStorageService;

  @tracked fileToUpload?: File | null | false;
  @tracked isInvalid = false;
  @tracked scale = 1;

  @localCopy('args.currentPhoto') currentPhoto!: EmployeesProfilePhotoUploaderModalSignature['Args']['currentPhoto'];
  @localCopy('args.directUploadURL', '/a/visitors/api/direct-uploads')
  directUploadURL!: EmployeesProfilePhotoUploaderModalSignature['Args']['directUploadURL'];
  @localCopy('args.prefix', '') prefix!: EmployeesProfilePhotoUploaderModalSignature['Args']['prefix'];
  @localCopy('args.validationError', 'Please select an image file')
  validationError!: EmployeesProfilePhotoUploaderModalSignature['Args']['validationError'];
  @localCopy('args.minimumScale', '1')
  minimumScale!: EmployeesProfilePhotoUploaderModalSignature['Args']['minimumScale'];
  @localCopy('args.maximumScale', '4')
  maximumScale!: EmployeesProfilePhotoUploaderModalSignature['Args']['maximumScale'];

  get inputsDisabled(): boolean {
    if (this.args.isLoading) return true;
    if (this.uploadTask.isRunning) return true;

    return false;
  }

  get saveDisabled(): boolean {
    if (this.inputsDisabled) return true;

    return isPresent(this.fileToUpload) ? false : true;
  }

  upload(): void {
    const { args, directUploadURL, prefix, activeStorageExtension, fileToUpload } = this;
    const { onUpload, onError } = args;
    const onUploadComplete = (value: string) => this.afterUploadSuccess(value);

    // ember-active-storage addon does not support extra params in the POST payload.
    // But API currently supports sending the prefix as a query param.
    const endpoint = prefix ? `${directUploadURL}?prefix=${prefix}` : directUploadURL;
    const task = this.uploadTask.perform(fileToUpload, endpoint, activeStorageExtension, { onUploadComplete, onError });

    onUpload?.(task);
  }

  afterUploadSuccess(value: string | null): void {
    this.fileToUpload = null;
    this.args.onUploadComplete?.(value);
  }

  @action
  close(): void {
    this.fileToUpload = null;
    this.args.onClose?.();
  }

  @action
  save(): void {
    if (this.fileToUpload instanceof File) {
      this.upload();
    } else if (this.fileToUpload === false) {
      this.afterUploadSuccess(null);
    }

    this.args.onSave?.();
  }

  @action
  remove(): void {
    this.currentPhoto = undefined;
    this.fileToUpload = false;
    this.isInvalid = false;

    if (this.args.uploadDisabled) {
      this.args.onRemoveRequest?.();
    } else {
      this.args.onRemove?.();
    }
  }

  @action
  handleFile(file: File): void {
    this.isInvalid = false;
    this.fileToUpload = file;
  }

  @action
  handleError(error: unknown): void {
    if (error === this.validationError) {
      this.isInvalid = true;
    } else {
      this.args.onError?.(error);
    }
  }

  // Prevent default behavior if file dropped outside of input
  @action
  handleDrop(evt: DragEvent): boolean {
    if (((<DragEvent & { originalTarget: HTMLInputElement }>evt).originalTarget ?? evt.srcElement).type === 'file')
      return true;

    evt.stopPropagation();
    evt.stopImmediatePropagation();
    evt.preventDefault();

    if (evt.dataTransfer) {
      evt.dataTransfer.effectAllowed = 'none';
      evt.dataTransfer.dropEffect = 'none';
    }

    return false;
  }

  @action
  updateScale(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.scale = parseFloat(target.value);
  }

  @action
  setMinimumScale(): void {
    this.scale = Number(this.minimumScale);
  }

  @action
  setMaximumScale(): void {
    this.scale = Number(this.maximumScale);
  }

  @enqueueTask uploadTask: {
    progress: number;
    isRunning?: boolean;
    perform: (
      file: File | null | false | undefined,
      endpoint: string | undefined,
      activeStorageExtension: ActiveStorageService,
      callbacks?: {
        onUploadComplete?: (value: string) => void;
        onError?: (error: string) => void;
      },
    ) => Generator<Promise<{ signedId: string }>, unknown, { signedId: string }>;
  } = {
    progress: 0,

    *perform(
      this: { context: EmployeesProfilePhotoUploaderModal; progress: number },
      file,
      endpoint,
      activeStorageExtension,
      callbacks = {},
    ): Generator<Promise<{ signedId: string }>, unknown, { signedId: string }> {
      const { onUploadComplete, onError } = callbacks;

      if (!file) {
        throw new Error('Upload halted: no file specified');
      }

      if (!endpoint) {
        throw new Error('Upload halted: no direct upload endpoint specified');
      }

      if (typeof activeStorageExtension.upload !== 'function') {
        throw new Error('Upload halted: invalid Active Storage service specified');
      }

      try {
        const { signedId } = yield activeStorageExtension.upload(file, endpoint, {
          onProgress: (progress) => {
            this.progress = progress;
          },
          onXHROpened: (xhr) => {
            try {
              xhr.withCredentials = this.context.args.includeCredentials ?? false;
            } catch (_) {
              // Fail silently
            }
          },
        });

        onUploadComplete?.(signedId);

        return signedId;
      } catch (error) {
        const message =
          typeof error === 'string'
            ? `Photo upload failed with the error "${error.toLowerCase()}"`
            : 'Photo upload failed';
        onError?.(message);

        return;
      }
    },
  };

  <template>
    <ModalDialog
      @clickOutsideToClose={{false}}
      @containerClass="!max-w-[462px] pointer-events-auto"
      @overlayClassNames="ember-modal-overlay--fadeIn pointer-events-none"
      @targetAttachment="center"
      @translucentOverlay={{true}}
    >
      <div
        class="center relative"
        {{on "dragenter" this.handleDrop}}
        {{on "dragover" this.handleDrop}}
        {{on "drop" this.handleDrop}}
        data-test-employee-photo-uploader
      >
        <div class="relative bg-carbon-70 text-white w-full h-[95vw] max-h-[462px]">
          {{#if @showCloseButton}}
            <button
              class="absolute top-6 right-6 z-10 btn btn--small btn--outline btn--cancel !bg-white !text-carbon-70"
              type="button"
              {{on "click" this.close}}
              data-test-employee-photo-uploader-close
            >
              &times;
            </button>
          {{/if}}
          <EmployeesProfilePhotoUploaderInput
            class="photo-mask w-full h-full overflow-hidden after:absolute after:top-[3%] after:left-[3%] after:w-[94%] after:h-[94%] after:rounded-full after:pointer-events-none"
            @currentPhoto={{this.currentPhoto}}
            @onFilePrepared={{this.handleFile}}
            @isDisabled={{this.inputsDisabled}}
            @onError={{this.handleError}}
            @outputSize={{@outputSize}}
            @scale={{this.scale}}
            @validationError={{this.validationError}}
            @validType={{@validType}}
          />
        </div>

        {{#if this.fileToUpload}}
          <label class="block flex justify-center items-center mt-4 -mb-2">
            <span class="sr-only">Scale photo</span>
            <div {{on "click" this.setMinimumScale}} data-test-employee-photo-uploader-scale-min>
              {{svgJar "photo-size-minimum"}}
            </div>
            <input
              type="range"
              value={{this.scale}}
              {{on "change" this.updateScale}}
              min={{this.minimumScale}}
              max={{this.maximumScale}}
              step="0.01"
              disabled={{this.inputsDisabled}}
              class="block w-2/3 mx-2 -mt-1"
              data-test-employee-photo-uploader-scale
            />
            <div {{on "click" this.setMaximumScale}} data-test-employee-photo-uploader-scale-max>
              {{svgJar "photo-size-maximum"}}
            </div>
          </label>
        {{else if (and this.validationError this.isInvalid)}}
          <div data-test-employee-photo-uploader-validation-error class="px-8 mt-8 text-carbon-50 text-sm">
            {{this.validationError}}
          </div>
        {{/if}}

        <div class="flex justify-end p-8">
          {{#if @uploadDisabled}}
            {{! When upload is disabled, show Cancel and Remove photo buttons }}
            <div class="flex-auto"></div>

            <button
              class="btn btn--small btn--outlineFill ml-4"
              type="button"
              disabled={{this.inputsDisabled}}
              {{on "click" this.close}}
              data-test-employee-photo-uploader-cancel
            >
              Cancel
            </button>

            {{#if (and this.currentPhoto @showRemoveButton)}}
              <button
                class="btn btn--small btn--brand ml-4"
                type="button"
                disabled={{this.inputsDisabled}}
                {{on "click" this.remove}}
                data-test-employee-photo-uploader-remove
              >
                Remove photo
              </button>
            {{/if}}
          {{else}}
            {{! Normal mode: show Remove, Cancel, and Save buttons }}
            {{#if this.currentPhoto}}
              <button
                class="btn btn--small btn--link !text-carbon-60 mr-4"
                type="button"
                disabled={{this.inputsDisabled}}
                {{on "click" this.remove}}
                data-test-employee-photo-uploader-remove
              >
                Remove photo
              </button>
            {{/if}}

            <div class="flex-auto"></div>

            <button
              class="btn btn--small btn--outlineFill ml-4"
              type="button"
              disabled={{this.inputsDisabled}}
              {{on "click" this.close}}
              data-test-employee-photo-uploader-cancel
            >
              Cancel
            </button>

            <button
              class="btn btn--small btn--brand ml-4"
              type="button"
              disabled={{this.saveDisabled}}
              {{on "click" this.save}}
              data-test-employee-photo-uploader-save
            >
              Save
            </button>
          {{/if}}
        </div>
      </div>
    </ModalDialog>

    {{! Ignore any file dropped outside the modal area; prevent browser default }}
    <div
      class="fixed w-full h-full top-0 left-0 opacity-0 z-40"
      {{on "dragenter" this.handleDrop}}
      {{on "dragover" this.handleDrop}}
      {{on "drop" this.handleDrop}}
    />
  </template>
}
